import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Steps,
  ConfigProvider,
} from "antd";
import { PlusOutlined, SaveOutlined, ReloadOutlined, DeleteOutlined, ArrowRightOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";

import { loadAllDepartment } from "../../redux/rtk/features/hrm/department/departmentSlice";
import { loadAllDesignation } from "../../redux/rtk/features/hrm/designation/designationSlice";
import { loadAllEmployeeStatus } from "../../redux/rtk/features/hrm/employeeStatus/employeeStatusSlice";
import { loadAllRole } from "../../redux/rtk/features/hr/role/roleSlice";
import { loadAllShift } from "../../redux/rtk/features/hrm/shift/shiftSlice";
import { addStaff, loadAllStaff } from "../../redux/rtk/features/hrm/user/userSlice";
import { loadAllWeeklyHoliday } from "@/redux/rtk/features/hrm/holiday/weeklyHolidaySlice";

import EmployeeEducationForm from "./EmployeeEducationForm";
import BigDrawer from "../Drawer/BigDrawer";
import AddDesignation from "../HRM/Designation/AddDesignation";
import AddEmploymentStatus from "../HRM/EmploymentStatus/AddEmploymentStatus";
import AddDepartment from "../HRM/Department/AddDepartment";
import AddShift from "../HRM/Shift/AddShift";
import AddWeeklyHoliday from "../HRM/WeeklyHoliday/AddWeeklyHoliday";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
const { Option } = Select;

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
const DATE_FORMAT = "YYYY-MM-DD";
const CONTROL_SIZE = "large";

export default function AddStaffStepTabs() {
  const nevigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [current, setCurrent] = useState(0);
  const [autoSubmitted, setAutoSubmitted] = useState(false);

  const { list: roles } = useSelector((s) => s.role);
  const { list: departments } = useSelector((s) => s.department);
  const { list: designations } = useSelector((s) => s.designations);
  const { list: employmentStatuses } = useSelector((s) => s.employmentStatus);
  const { list: shifts } = useSelector((s) => s.shift);
  const { list: weeklyHolidays, loading: weekLoading } = useSelector((s) => s.weeklyHoliday);

  useEffect(() => {
    dispatch(loadAllRole());
    dispatch(loadAllDepartment());
    dispatch(loadAllDesignation({ query: "all" }));
    dispatch(loadAllShift());
    dispatch(loadAllEmployeeStatus());
    dispatch(loadAllWeeklyHoliday());
  }, [dispatch]);

  const onReset = () => {
    form.resetFields();
    setCurrent(0);
    setAutoSubmitted(false);
  };

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      const education = (values.education || []).map((e) => ({
        degree: e.degree?.trim() || null,
        institution: e.institute?.trim() || null,       // institute -> institution
        fieldOfStudy: e.board?.trim() || null,          // board -> fieldOfStudy
        result: e.result?.trim() || null,
        studyStartDate: e.startYear
          ? dayjs(`${e.startYear}-01-01`).format("YYYY-MM-DD HH:mm:ss")
          : null,
        studyEndDate: e.endYear
          ? dayjs(`${e.endYear}-01-01`).format("YYYY-MM-DD HH:mm:ss")
          : null,
      }));

      const payload = {
        ...values,
        joinDate: values.joinDate ? dayjs(values.joinDate).format("YYYY-MM-DD HH:mm:ss") : null,
        leaveDate: values.leaveDate ? dayjs(values.leaveDate).format("YYYY-MM-DD HH:mm:ss") : null,
        designationStartDate: values.designationStartDate ? dayjs(values.designationStartDate).format("YYYY-MM-DD HH:mm:ss") : null,
        designationEndDate: values.designationEndDate ? dayjs(values.designationEndDate).format("YYYY-MM-DD HH:mm:ss") : null,
        salaryStartDate: values.salaryStartDate ? dayjs(values.salaryStartDate).format("YYYY-MM-DD HH:mm:ss") : null,
        salaryEndDate: values.salaryEndDate ? dayjs(values.salaryEndDate).format("YYYY-MM-DD HH:mm:ss") : null,
        education, // mapped
      };

      const resp = await dispatch(addStaff(payload));
      if (resp.payload?.message === "success") {
        dispatch(loadAllStaff({ status: "true", count: 10, page: 1 }));
        form.resetFields();
        setCurrent(0);
        setAutoSubmitted(false);
        nevigate("/admin/staff");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const joinDate = Form.useWatch("joinDate", form);
  const disabledLeaveDate = (currentDate) => {
    if (!joinDate) return false;
    return currentDate && currentDate.isBefore(joinDate, "day");
  };

  const roleOptions = useMemo(() => (roles || []).map((r) => ({ label: r.name, value: r.id })), [roles]);
  const departmentOptions = useMemo(() => (departments || []).map((d) => ({ label: d.name, value: d.id })), [departments]);
  const designationOptions = useMemo(() => (designations || []).map((d) => ({ label: d.name, value: d.id })), [designations]);
  const statusOptions = useMemo(() => (employmentStatuses || []).map((e) => ({ label: e.name, value: e.id })), [employmentStatuses]);
  const shiftOptions = useMemo(() => (shifts || []).map((s) => ({ label: s.name, value: s.id })), [shifts]);
  const weeklyHolidayOptions = useMemo(() => (weeklyHolidays || []).map((w) => ({ label: w.name, value: w.id })), [weeklyHolidays]);

  const steps = [
    {
      key: "user",
      title: "User Information",
      icon: "👤",
      fields: [
        "firstName",
        "lastName",
        "username",
        "password",
        "email",
        "phone",
        "street",
        "city",
        "state",
        "zipCode",
        "country",
      ],
      content: (
        <div className="space-y-5 md:space-y-6">
          <div>
            <h3 className="text-lg md:text-xl font-semibold text-slate-800 mb-1">User Information</h3>
            <p className="text-xs md:text-sm text-slate-500 mb-4 md:mb-6">Enter basic user details and credentials</p>

            <div className="space-y-3 md:space-y-4">
              <Row gutter={[12, 8]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span className="text-sm font-medium text-slate-700">First Name</span>}
                    name="firstName"
                    rules={[{ required: true, message: "Please input first name" }]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Input size={CONTROL_SIZE} placeholder="Enter first name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span className="text-sm font-medium text-slate-700">Last Name</span>}
                    name="lastName"
                    rules={[{ required: true, message: "Please input last name" }]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Input size={CONTROL_SIZE} placeholder="Enter last name" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[12, 8]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span className="text-sm font-medium text-slate-700">Username</span>}
                    name="username"
                    rules={[{ required: true, message: "Please input username" }]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Input size={CONTROL_SIZE} placeholder="Enter username" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span className="text-sm font-medium text-slate-700">Password</span>}
                    name="password"
                    rules={[{ required: true, message: "Please input a password" }]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Input.Password size={CONTROL_SIZE} placeholder="Enter password" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[12, 8]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span className="text-sm font-medium text-slate-700">Email</span>}
                    name="email"
                    rules={[
                      { required: true, message: "Please input email" },
                      { type: "email", message: "Provide a valid email" },
                    ]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Input size={CONTROL_SIZE} placeholder="Enter email address" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span className="text-sm font-medium text-slate-700">Phone</span>}
                    name="phone"
                    rules={[{ required: true, message: "Please input phone number" }]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Input size={CONTROL_SIZE} placeholder="Enter phone number" maxLength={20} />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </div>

          <div className="pt-3 md:pt-4 border-t border-slate-200">
            <h3 className="text-lg md:text-xl font-semibold text-slate-800 mb-1">Address Information</h3>
            <p className="text-xs md:text-sm text-slate-500 mb-4 md:mb-6">Provide residential address details</p>

            <div className="space-y-3 md:space-y-4">
              <Row gutter={[12, 8]}>
                <Col xs={24} md={12}>
                  <Form.Item label={<span className="text-sm font-medium text-slate-700">Street</span>} name="street" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                    <Input size={CONTROL_SIZE} placeholder="Enter street address" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={<span className="text-sm font-medium text-slate-700">City</span>} name="city" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                    <Input size={CONTROL_SIZE} placeholder="Enter city" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[12, 8]}>
                <Col xs={24} md={12}>
                  <Form.Item label={<span className="text-sm font-medium text-slate-700">State</span>} name="state" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                    <Input size={CONTROL_SIZE} placeholder="Enter state" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={<span className="text-sm font-medium text-slate-700">Zip Code</span>} name="zipCode" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                    <Input size={CONTROL_SIZE} placeholder="Enter zip code" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label={<span className="text-sm font-medium text-slate-700">Country</span>} name="country" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                <Input size={CONTROL_SIZE} placeholder="Enter country" />
              </Form.Item>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "employment",
      title: "Employment Information",
      icon: "✓",
      fields: [
        "joinDate",
        "leaveDate",
        "employeeId",
        "bloodGroup",
        "employmentStatusId",
        "departmentId",
        "roleId",
        "shiftId",
        "weeklyHolidayId",
        "designationId",
        "designationStartDate",
        "designationEndDate",
        "salary",
        "salaryStartDate",
        "salaryEndDate",
        "salaryComment",
        "commissionType",
        "commissionValue",
      ],
      content: (
        <div className="space-y-5 md:space-y-6">
          <div>
            <h3 className="text-lg md:text-xl font-semibold text-slate-800 mb-1">Employment Information</h3>
            <p className="text-xs md:text-sm text-slate-500 mb-4 md:mb-6">Configure employment details and organizational settings</p>

            <div className="space-y-3 md:space-y-4">
              <Row gutter={[12, 8]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span className="text-sm font-medium text-slate-700">Joining Date</span>}
                    name="joinDate"
                    rules={[{ required: true, message: "Please select joining date" }]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <DatePicker size={CONTROL_SIZE} className="w-full" format={DATE_FORMAT} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={<span className="text-sm font-medium text-slate-700">Leave Date</span>} name="leaveDate" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                    <DatePicker size={CONTROL_SIZE} className="w-full" format={DATE_FORMAT} disabledDate={disabledLeaveDate} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[12, 8]}>
                <Col xs={24} md={12}>
                  <Form.Item label={<span className="text-sm font-medium text-slate-700">Employee ID</span>} name="employeeId" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                    <Input size={CONTROL_SIZE} placeholder="Enter employee ID" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={<span className="text-sm font-medium text-slate-700">Blood Group</span>} name="bloodGroup" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                    <Select size={CONTROL_SIZE} placeholder="Select blood group" allowClear showSearch>
                      {BLOOD_GROUPS.map((b) => (
                        <Option key={b} value={b}>{b}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[12, 8]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <Space size={2}>
                        <span className="text-sm font-medium text-slate-700">Employee Status</span>
                        <BigDrawer title="New Employee Status">
                          <AddEmploymentStatus drawer="true" />
                        </BigDrawer>
                      </Space>
                    }
                    name="employmentStatusId"
                    rules={[{ required: true, message: "Please select employment status" }]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Select size={CONTROL_SIZE} placeholder="Select status" options={statusOptions} allowClear optionFilterProp="label" showSearch />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <Space size={2}>
                        <span className="text-sm font-medium text-slate-700">Department</span>
                        <BigDrawer title="New Department">
                          <AddDepartment drawer="true" />
                        </BigDrawer>
                      </Space>
                    }
                    name="departmentId"
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Select size={CONTROL_SIZE} placeholder="Select department" options={departmentOptions} optionFilterProp="label" allowClear showSearch />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[12, 8]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span className="text-sm font-medium text-slate-700">Role</span>}
                    name="roleId"
                    rules={[{ required: true, message: "Please select role" }]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Select size={CONTROL_SIZE} placeholder="Select role" options={roleOptions} allowClear showSearch />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <Space size={2}>
                        <span className="text-sm font-medium text-slate-700">Shift</span>
                        <BigDrawer title="New Shift">
                          <AddShift drawer="true" />
                        </BigDrawer>
                      </Space>
                    }
                    name="shiftId"
                    rules={[{ required: true, message: "Please select shift" }]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Select size={CONTROL_SIZE} placeholder="Select shift" options={shiftOptions} allowClear optionFilterProp="label" showSearch />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[12, 8]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <Space size={2}>
                        <span className="text-sm font-medium text-slate-700">Weekly Holiday</span>
                        <BigDrawer title="New Weekly Holiday">
                          <AddWeeklyHoliday drawer="true" />
                        </BigDrawer>
                      </Space>
                    }
                    name="weeklyHolidayId"
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Select size={CONTROL_SIZE} placeholder="Select weekly holiday" options={weeklyHolidayOptions}
                      optionFilterProp="label" allowClear showSearch />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <Space size={2}>
                        <span className="text-sm font-medium text-slate-700">Designation</span>
                        <BigDrawer title="Designation">
                          <AddDesignation drawer={true} />
                        </BigDrawer>
                      </Space>
                    }
                    name="designationId"
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Select size={CONTROL_SIZE} placeholder="Select designation" options={designationOptions} optionFilterProp="label" allowClear showSearch />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </div>

          <div className="pt-3 md:pt-4 border-t border-slate-200">
            <h3 className="text-lg md:text-xl font-semibold text-slate-800 mb-1">Designation & Salary Information</h3>
            <p className="text-xs md:text-sm text-slate-500 mb-4 md:mb-6">Set salary and commission information</p>

            <div className="space-y-3 md:space-y-4">
              <Row gutter={[12, 8]}>
                <Col xs={24} md={12}>
                  <Form.Item label={<span className="text-sm font-medium text-slate-700">Designation Start Date</span>} name="designationStartDate" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                    <DatePicker size={CONTROL_SIZE} className="w-full" format={DATE_FORMAT} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={<span className="text-sm font-medium text-slate-700">Designation End Date</span>} name="designationEndDate" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                    <DatePicker size={CONTROL_SIZE} className="w-full" format={DATE_FORMAT} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[12, 8]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span className="text-sm font-medium text-slate-700">Salary</span>}
                    name="salary"
                    rules={[{ required: true, message: "Please input salary" }]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <InputNumber size={CONTROL_SIZE} className="w-full" min={0} placeholder="Enter salary amount" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span className="text-sm font-medium text-slate-700">Salary Start Date</span>}
                    name="salaryStartDate"
                    rules={[{ required: true, message: "Please select start date" }]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <DatePicker size={CONTROL_SIZE} className="w-full" format={DATE_FORMAT} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[12, 8]}>
                <Col xs={24} md={12}>
                  <Form.Item label={<span className="text-sm font-medium text-slate-700">Salary End Date</span>} name="salaryEndDate" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                    <DatePicker size={CONTROL_SIZE} className="w-full" format={DATE_FORMAT} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={<span className="text-sm font-medium text-slate-700">Commission Type</span>} name="commissionType" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                    <Select size={CONTROL_SIZE} allowClear placeholder="Select commission type">
                      <Option value="percentage">Percentage</Option>
                      <Option value="flat">Flat</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[12, 8]}>
                <Col xs={24} md={12}>
                  <Form.Item label={<span className="text-sm font-medium text-slate-700">Commission Value</span>} name="commissionValue" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                    <Input size={CONTROL_SIZE} placeholder="Enter commission value" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={<span className="text-sm font-medium text-slate-700">Salary Comment</span>} name="salaryComment" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                    <Input.TextArea size={CONTROL_SIZE} placeholder="Additional notes" autoSize={{ minRows: 1, maxRows: 3 }} />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "education",
      title: "Education Information",
      icon: "📚",
      fields: ["education"],
      content: (
        <div className="space-y-5 md:space-y-6">
          <div>
            <h3 className="text-lg md:text-xl font-semibold text-slate-800 mb-1">Education Information</h3>
            <p className="text-xs md:text-sm text-slate-500 mb-4 md:mb-6">Add educational qualifications and certifications</p>

            <Form.List name="education">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} className="bg-slate-50 border border-slate-200 rounded-lg p-4 md:p-5 mb-3 md:mb-4">
                      <div className="space-y-3 md:space-y-4">
                        <Row gutter={[12, 8]}>
                          <Col xs={24} md={12}>
                            <Form.Item
                              {...restField}
                              label={<span className="text-sm font-medium text-slate-700">Degree / Qualification</span>}
                              name={[name, "degree"]}
                              rules={[{ required: true, message: "Please input degree name" }]}
                              labelCol={{ span: 24 }}
                              wrapperCol={{ span: 24 }}
                            >
                              <Input size={CONTROL_SIZE} placeholder="e.g. Bachelor of Science" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              {...restField}
                              label={<span className="text-sm font-medium text-slate-700">Institute / University</span>}
                              name={[name, "institute"]}
                              rules={[{ required: true, message: "Please input institute name" }]}
                              labelCol={{ span: 24 }}
                              wrapperCol={{ span: 24 }}
                            >
                              <Input size={CONTROL_SIZE} placeholder="e.g. University Name" />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row gutter={[12, 8]}>
                          <Col xs={24} md={12}>
                            <Form.Item {...restField} label={<span className="text-sm font-medium text-slate-700">Board / Department</span>} name={[name, "board"]} labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                              <Input size={CONTROL_SIZE} placeholder="e.g. Computer Science" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item {...restField} label={<span className="text-sm font-medium text-slate-700">Result / GPA</span>} name={[name, "result"]} labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                              <Input size={CONTROL_SIZE} placeholder="e.g. 3.85 / 4.00" />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row gutter={[12, 8]}>
                          <Col xs={24} md={12}>
                            <Form.Item {...restField} label={<span className="text-sm font-medium text-slate-700">Start Year</span>} name={[name, "startYear"]} labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                              <Input size={CONTROL_SIZE} placeholder="2018" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item {...restField} label={<span className="text-sm font-medium text-slate-700">End Year</span>} name={[name, "endYear"]} labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                              <Input size={CONTROL_SIZE} placeholder="2022" />
                            </Form.Item>
                          </Col>
                        </Row>

                        <div className="text-right pt-1 md:pt-2">
                          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} size="large">
                            Remove this record
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}

                  <Form.Item wrapperCol={{ span: 24 }}>
                    <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add()} size="large" className="h-11 md:h-12">
                      Add Education Information
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </div>
        </div>
      ),
    },
    {
      key: "review",
      title: "Submit",
      icon: "✉️",
      fields: [],
      content: (
        <div className="text-center py-10 md:py-12">
          <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-green-100 mb-3 md:mb-4">
            <svg className="w-7 h-7 md:w-8 md:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl md:text-2xl font-semibold text-slate-800 mb-2">Ready to Submit</h3>
          <p className="text-slate-600 mb-4 md:mb-6">Please review all the information before submitting</p>
          <p className="text-xs md:text-sm text-slate-500">Click the  Save Employee  button below to complete the process</p>
        </div>
      ),
    },
  ];

  const next = async () => {
    const step = steps[current];
    if (step?.fields?.length) {
      await form.validateFields(step.fields);
    }
    setCurrent((c) => Math.min(c + 1, steps.length - 1));
  };

  const prev = () => setCurrent((c) => Math.max(c - 1, 0));

  useEffect(() => {
    const isReview = current === steps.length - 1;
    if (isReview && !autoSubmitted) {
      setAutoSubmitted(true);
    }
  }, [current, steps.length, autoSubmitted]);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#15B983",
          borderRadius: 8,
          fontSize: 14,
        },
        components: {
          Steps: {
            colorPrimary: "#15B983",
          },
          Input: {
            controlHeight: 44,
          },
          Select: {
            controlHeight: 44,
          },
          DatePicker: {
            controlHeight: 44,
          },
          Button: {
            controlHeight: 44,
            fontWeight: 500,
          },
        },
      }}
    >
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-6 md:py-8">
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1.5 md:mb-2">Add New Employee</h1>
            <p className="text-slate-600 text-sm md:text-base">Complete all steps to add a new employee to the system</p>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 md:gap-6">
            {/* Left Sidebar - Steps */}
            <div className="lg:sticky lg:top-6 h-fit">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 md:p-6">
                <div className="space-y-1.5 md:space-y-2 mb-4 md:mb-6">
                  {steps.map((step, idx) => (
                    <div
                      key={step.key}
                      className={`flex items-center gap-3 p-2.5 md:p-3 rounded-lg transition-all cursor-pointer ${current === idx
                        ? "bg-green-50 border border-green-200"
                        : current > idx
                          ? "bg-slate-50 border border-slate-200"
                          : "border border-transparent hover:bg-slate-50"
                        }`}
                      onClick={() => {
                        if (idx < current) setCurrent(idx);
                      }}
                    >
                      <div
                        className={`flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full text-base md:text-lg flex-shrink-0 ${current === idx
                          ? "bg-[#15B983] text-white"
                          : current > idx
                            ? "bg-green-100 text-green-700"
                            : "bg-slate-100 text-slate-400"
                          }`}
                      >
                        {current > idx ? "✓" : step.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] md:text-xs font-medium text-slate-500 mb-0.5">
                          Step {idx + 1}
                        </div>
                        <div
                          className={`text-sm font-semibold truncate ${current === idx ? "text-slate-800" : "text-slate-600"
                            }`}
                        >
                          {step.title}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Content - Form */}
            <div>
              <Form
                form={form}
                layout="vertical"
                size={CONTROL_SIZE}
                onFinish={onFinish}
                scrollToFirstError
                initialValues={{}}
              >
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 md:p-8 mb-4 md:mb-6">
                  {steps.map((s, idx) => (
                    <div key={s.key} className={current === idx ? "block" : "hidden"}>
                      {s.content}
                    </div>
                  ))}
                </div>

                {/* Footer Actions */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <Button
                      onClick={onReset}
                      icon={<ReloadOutlined />}
                      size="large"
                      className="w-full sm:w-auto"
                    >
                      Reset Form
                    </Button>

                    {/* Mobile: buttons fill width; Desktop: auto width, right-aligned */}
                    <div className="w-full sm:w-auto flex gap-3 sm:justify-end">
                      {current > 0 && current < steps.length - 1 && (
                        <Button
                          onClick={prev}
                          size="large"
                          icon={<ArrowLeftOutlined />}
                          className="w-full sm:w-auto flex-1 sm:flex-none"
                        >
                          Previous
                        </Button>
                      )}

                      {current < steps.length - 2 && (
                        <Button
                          type="primary"
                          onClick={next}
                          size="large"
                          icon={<ArrowRightOutlined />}
                          iconPosition="end"
                          className="w-full sm:w-auto flex-1 sm:flex-none"
                        >
                          Next Step
                        </Button>
                      )}

                      {current === steps.length - 2 && (
                        <Button
                          type="primary"
                          onClick={next}
                          loading={submitting}
                          size="large"
                          icon={<ArrowRightOutlined />}
                          iconPosition="end"
                          className="w-full sm:w-auto flex-1 sm:flex-none"
                        >
                          Review & Submit
                        </Button>
                      )}

                      {current === steps.length - 1 && (
                        <Button
                          type="primary"
                          icon={<SaveOutlined />}
                          onClick={async () => {
                            try {
                              await form.validateFields();
                              form.submit();
                            } catch (err) {
                              console.error("Validation failed:", err);
                            }
                          }}
                          loading={submitting}
                          size="large"
                          className="w-full sm:w-auto flex-1 sm:flex-none"
                        >
                          Save Employee
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .ant-input,
        .ant-input-number,
        .ant-picker,
        .ant-select .ant-select-selector {
          border-radius: 6px !important;
        }

        .ant-btn {
          border-radius: 8px !important;
          font-weight: 500;
        }

        // .ant-form-item {
        //   margin-bottom: 0 !important;
        // }

        .ant-form-item-label > label {
          height: auto !important;
        }
      `}</style>
    </ConfigProvider>
  );
}
